import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ApolloError, NetworkStatus, useApolloClient } from '@apollo/client';
import GettextFactory from '../../../util/GettextFactory';
import {
    type GetEligiblePastDqAnswersQuery,
    useGetEligiblePastDqAnswersQuery,
    useSetPastAnswersDqPrefillConsentMutation,
} from './__generated__/PastAnswers.generated';
import type { PastAnswerItem } from './PastAnswers';
import {
    PAST_ANSWERS_PAGE_SIZE,
    PastAnswersPageContent,
    type PastAnswersPageConnection,
} from './PastAnswersPageContent';
import {
    type AutoApplySetupRumErrorContext,
    AutoApplySetupRumContext as RumContext,
    logAutoApplySetupRumError,
} from '../autoApplySetupRumErrors';

type DqPastAnswerNode = NonNullable<
    GetEligiblePastDqAnswersQuery['eligiblePastDQAnswers']
>['edges'][number]['node'];

const adaptDqPastAnswer = (node: DqPastAnswerNode): PastAnswerItem => ({
    question: {
        intentId: node.intentId,
        type: node.question.type,
        question: node.question.text ?? '',
        options: node.question.options.map((option) => ({
            value: option.value,
            label: option.label ?? option.value,
        })),
    },
    answers: [{ values: node.answer.values, createdAt: node.updatedAt }],
    domain: node.domain,
});

export const DqPastAnswersPage: React.FC = () => {
    const gt = GettextFactory();
    const apolloClient = useApolloClient();
    const [mutationFailure, setMutationFailure] = useState<{
        error: Error;
        isVerificationError: boolean;
    }>();
    const mutationError = mutationFailure?.error;
    const reportedErrors = useRef<{
        pastAnswers?: Error;
        mutation?: Error;
    }>({});
    const {
        data,
        loading,
        networkStatus,
        error: pastAnswersError,
        refetch: refetchPastAnswers,
        fetchMore,
    } = useGetEligiblePastDqAnswersQuery({
        variables: { first: PAST_ANSWERS_PAGE_SIZE },
        notifyOnNetworkStatusChange: true,
        ssr: false,
    });
    const [setDqPrefillConsent, { loading: saving }] = useSetPastAnswersDqPrefillConsentMutation();

    useEffect(() => {
        const reportOnce = (
            key: keyof typeof reportedErrors.current,
            error: Error | undefined,
            errorContext: AutoApplySetupRumErrorContext,
            isPageFailure?: boolean,
        ): void => {
            if (!error) {
                delete reportedErrors.current[key];
            } else if (reportedErrors.current[key] !== error) {
                reportedErrors.current[key] = error;
                logAutoApplySetupRumError(error, errorContext, isPageFailure);
            }
        };

        reportOnce('pastAnswers', pastAnswersError, RumContext.PastAnswersLoad);
        reportOnce(
            'mutation',
            mutationError,
            RumContext.PastAnswersAction,
            mutationFailure?.isVerificationError,
        );
    }, [pastAnswersError, mutationError, mutationFailure?.isVerificationError]);

    const apiConnection = data?.eligiblePastDQAnswers;
    const connection: PastAnswersPageConnection | undefined = apiConnection
        ? {
              edges: apiConnection.edges.map((edge) => adaptDqPastAnswer(edge.node)),
              pageInfo: apiConnection.pageInfo,
              totalCount: apiConnection.totalCount,
          }
        : undefined;
    // Unknown consent must not appear as either an opted-in or opted-out preference.
    const usePastAnswers = apiConnection ? apiConnection.consent === true : undefined;

    const handleUsePastAnswersChange = useCallback(
        async (checked: boolean): Promise<boolean> => {
            setMutationFailure(undefined);
            let isVerificationError = false;

            try {
                const result = await setDqPrefillConsent({
                    variables: { consent: checked },
                });

                if (result.data?.setDqPrefillConsent?.consent !== checked) {
                    isVerificationError = true;
                    throw new Error(
                        'DQ prefill consent response did not match the requested value',
                    );
                }

                apolloClient.cache.modify({
                    fields: {
                        eligiblePastDQAnswers(existingConnection) {
                            if (!existingConnection) return existingConnection;
                            return {
                                ...existingConnection,
                                consent: checked,
                                ...(checked ? {} : { edges: [], totalCount: 0 }),
                            };
                        },
                    },
                });
                if (checked) {
                    // Refresh from the server, never restore answers purged on opt-out.
                    // Query errors are surfaced by pastAnswersError, not mutationError.
                    void refetchPastAnswers().catch(() => {});
                }
                return true;
            } catch (error) {
                const normalizedError =
                    error instanceof Error
                        ? error
                        : new Error('Failed to update DQ prefill consent');
                setMutationFailure({ error: normalizedError, isVerificationError });
                console.error('Failed to update DQ prefill consent:', error);
                return false;
            }
        },
        [apolloClient, refetchPastAnswers, setDqPrefillConsent],
    );

    const onFetchMore = useCallback(
        (first: number, after: string) =>
            fetchMore({ variables: { first, after } })
                .then((result) => {
                    // errorPolicy: 'all' resolves GraphQL failures instead of rejecting.
                    if (result.errors?.length) {
                        throw new ApolloError({ graphQLErrors: result.errors });
                    }
                    return result;
                })
                .catch((error: unknown) => {
                    // fetchMore failures need not populate the query's error field.
                    logAutoApplySetupRumError(error, RumContext.PastAnswersLoadMore, false);
                    throw error;
                }),
        [fetchMore],
    );
    const onRetrySource = useCallback(() => {
        if (pastAnswersError) void refetchPastAnswers().catch(() => {});
        if (mutationError) void handleUsePastAnswersChange(!usePastAnswers);
    }, [
        pastAnswersError,
        mutationError,
        refetchPastAnswers,
        handleUsePastAnswersChange,
        usePastAnswers,
    ]);

    return (
        <PastAnswersPageContent
            connection={connection}
            usePastAnswers={usePastAnswers}
            onUsePastAnswersChange={handleUsePastAnswersChange}
            toggleDisabled={saving}
            headerTitle={gt.gettext('Voluntary self-identification questions')}
            toggleLabel={gt.gettext('Autofill voluntary self-identification questions')}
            fetching={loading || networkStatus === NetworkStatus.fetchMore}
            sourceError={Boolean(pastAnswersError || mutationError)}
            onRetrySource={onRetrySource}
            onFetchMore={onFetchMore}
        />
    );
};
